/* Owlkrown hero animations.
 *
 * Each entry finds its slide by the filename of that slide's own background,
 * never by position. Slides get dragged around in the theme editor, and an
 * index-based lookup silently paints one product's animation onto another's
 * slide: that is exactly what happened on 2026-10-02 when the No Slice slide
 * was moved to the front and inherited the SL3 FlexPlate sequence.
 *
 * Every hero is a PANEL now: media boxed in the left 56% of the slide with a
 * solid ground under the copy on the right, except SteelCore which is the same
 * shape with a video in the box. A full-bleed crossfade is just a panel with
 * split: 100, so there is no second code path for one.
 *
 * Frames that have been RE-UPLOADED carry a ?v= stamp, and the stamp must be
 * bumped every time that frame is replaced again. The opposite was assumed here
 * until 2026-10-11 and it is wrong: an un-stamped cdn.shopify.com URL is served
 * with max-age=31557600, so swapping the file under the same name leaves the
 * edge handing out the OLD image for up to a year. It refreshed for the four
 * sl3 mobile frames within seconds and never refreshed for sl3-desk-3 and -4,
 * so a replacement without a new URL ships a mix of old and new to visitors.
 * A frame that has only ever been uploaded once needs no stamp. The corollary:
 * to REORDER a sequence, renumber the map below; to re-grade one, upload under
 * a new name. Never re-upload different pixels under an existing name.
 *
 * A sequence runs when ITS OWN SLIDE becomes the selected one, never merely
 * when the hero scrolls into view. The hero is a FADE carousel: all six slides
 * sit in the same box and are hidden by opacity alone, so an
 * IntersectionObserver reports every one of them as fully visible the moment
 * the hero appears. Triggering on that ran all three sequences at once on page
 * load, behind slide 1, and every one of them was finished by 8s - long before
 * its own slide came round. Reaching the ProKit slide at 30s showed a still of
 * its LAST frame, the all-colours shot, which is what Indre reported on
 * 2026-10-13. Opacity is invisible to IntersectionObserver; only the
 * is-selected class says which slide a visitor is actually looking at.
 */
(function () {
  var CDN = 'https://cdn.shopify.com/s/files/1/0730/8489/5472/files/';

  /* ---- split slides -------------------------------------------------------
   * Six seconds of the cut test, trimmed from C8375.MP4 at 4-10s with the audio
   * track removed, running in the left 56% of the slide with a solid panel on
   * the right. The copy already sits at x 0.55-0.91, so it lands on the panel.
   *
   * It is a panel and not an overlay on purpose: the footage is handheld and
   * gloved arms sweep through the right of frame, so white copy laid straight
   * over it would flicker between readable and not, frame by frame.
   *
   * Desktop and mobile get their own rendition and only the visible one is ever
   * fetched - the hidden one stays preload="none" and is never told to play. */
  var VID = 'https://cdn.shopify.com/videos/c/vp/ec405b536f3f4493b718161d6b8f146f/ec405b536f3f4493b718161d6b8f146f';
  var SPLITS = [
    {
      id: 'nscut',
      deskKey: 'no-slice-desk-1',
      mobKey: 'no-slice-mob-1',
      desk: VID + '.HD-720p-4.5Mbps-96983938.mp4',
      mob:  VID + '.SD-480p-1.5Mbps-96983938.mp4',
      split: 56,        /* percent of the slide width given to the video */
      focus: '45% 45%'  /* 16:9 source in a narrower panel: this picks what survives */
    }
  ];

  /* ---- panel slides ------------------------------------------------------
   * Same shape as the cut-test slide above, with stills crossfading in the box
   * instead of a video, so this needs the stack and the panel together and
   * gets its own builder.
   *
   * Frame 1 is the base layer and never animates, so the panel is a picture
   * from the first paint rather than black until the first dissolve lands.
   *
   * Frames are cut to the BOX, 1120x1080, not to the slide. A frame composed
   * for a 2000x1080 full-bleed slide has empty space on the right for the type
   * that is simply wasted here, which is what the 2026-10-11 SL3 recut fixed.
   *
   * FIVE frames is the ceiling at the agreed pace. The fifth starts its
   * dissolve at 7.2s and lands at 8.0s, inside the 10s a slide is on screen; a
   * sixth would land at exactly 10.0s and never be seen. A FOUR frame set ends
   * by returning to frame 1 at 7.2s instead, so it keeps moving for the whole
   * 10s rather than holding a dead last frame for four of them.
   *
   * CROP ONLY is the standing rule for these panels. Three attempts at tone
   * work have now been built and withdrawn (two on ProKit, one on M10) and the
   * photographs won every time. Do not start a fourth without being asked. */
  var PANELS = [
    {
      id: 'qfc',
      deskKey: 'q-desk-1',
      mobKey: 'q-mob-1',
      /* Added 2026-10-21. FULL BLEED, split 100, which is why there is no
       * ok-sp under the copy on this one: Indre pulled the black panel for
       * this slide and asked for the long background the hero had before it,
       * the shape the SteelCore slide used to have.
       *
       * That is only possible because of where the camera sits. Every frame is
       * cut so the camera starts 6% in from the left edge and ends between
       * 0.48 and 0.61, and the copy column starts at 0.72, so bare wall
       * separates the two on all five. The crops are therefore NOT full width:
       * cropping from the LEFT is what walks the subject leftward in the
       * frame, so these are 1366 to 1540 wide out of 2000 rather than the
       * whole file. A 1.13x size change across the five, the tightest spread
       * of any hero here.
       *
       * THE COPY ON THIS SLIDE IS BLACK, set as text_color in the block rather
       * than in CSS. The wall behind it runs 142 to 161, so white measured 2.3
       * to 3.5:1 and failed the 3:1 large-text floor on four frames of five;
       * black measures 6.1 to 9.1:1 on the same pixels. Anything that moves
       * these crops needs that re-measured, and a panel is not the answer
       * here - it is the thing being removed.
       *
       * Cropped only, nothing darkened. The column width lives in custom.css,
       * keyed to this slide's CTA like every other per-slide rule. */
      desk: { 1: 'q-desk-1.webp',   /* front, grip and leather */
              2: 'q-desk-2.webp',   /* side, the grip standing proud */
              3: 'q-desk-3.webp',   /* back, screen clear */
              4: 'q-desk-4.webp',   /* underside, Arca profile */
              5: 'q-desk-5.webp' }, /* battery and card out */
      mob:  { 1: 'q-mob-1.webp',  2: 'q-mob-2.webp',  3: 'q-mob-3.webp',
              4: 'q-mob-4.webp',  5: 'q-mob-5.webp' },
      split: 100,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'sl3',
      deskKey: 'sl3n-desk-1',
      mobKey: 'sl3n-mob-1',
      /* FULL BLEED since 2026-10-22, split 100, so no ok-sp under the copy.
       * Indre pulled the black panel from this slide the same way she pulled
       * it from the Q FlexCase one.
       *
       * Four photographs over five beats: the opening shot comes back as the
       * fifth, her call, so the loop closes on itself instead of parking on a
       * last frame. That is what the trailing { f: 1 } step is.
       *
       * This shoot was composed for a right-hand copy column, which the Q set
       * was not: the camera and hand end between 0.49 and 0.57 of the frame on
       * their own, so these are the FULL 2000px width of each file cut to
       * 2000x1080, with no left crop and no change of scale between frames.
       *
       * The vertical crop is as generous as the files allow and no more. The
       * subject runs 1031 to 1069px tall inside a frame that can only be
       * 1080, so there is 11 to 49px of slack in total and it is split evenly
       * top and bottom. An earlier cut took 199px off the top and sliced the
       * lens off every frame; the mistake was measuring the subject's top
       * across the whole width, which caught the ARM running off the top-left
       * corner and read as "already at the edge". Measure the camera in a band
       * right of the hand, or make the same cut again.
       *
       * BLACK COPY, set as text_color on the block. The wall behind the copy
       * runs 159 to 167, so white measured 2.4 to 2.6:1 and black measures 8.0
       * to 8.8:1. The old panel is what made white work here; it is gone.
       *
       * The old 56% panel frames (sl3p-desk-1..4) and the first-generation
       * sl3-desk/sl3-mob files are still in Shopify Files and nothing points at
       * them any more. */
      desk: { 1: 'sl3n-desk-1.webp',   /* underside, card door open */
              2: 'sl3n-desk-2.webp',   /* same hold, turned to the plate */
              3: 'sl3n-desk-3.webp',   /* two hands, Arca rail along the base */
              4: 'sl3n-desk-4.webp' }, /* open palm, the whole plate */
      mob:  { 1: 'sl3n-mob-1.webp',  2: 'sl3n-mob-2.webp',
              3: 'sl3n-mob-3.webp',  4: 'sl3n-mob-4.webp' },
      split: 100,
      fade: 0.8,
      /* 1.2s still, 0.8s dissolve, back to frame 1 at 7.2s */
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 1, d: 7.2 }
      ]
    },
    {
      id: 'analog',
      deskKey: 'anlg-desk-1',
      mobKey: 'anlg-mob-1',
      /* FULL BLEED from 2026-10-22, split 100, fourth slide to lose its panel.
       *
       * Indre's order, unchanged from the panel version and still the reason
       * this sequence works: each flat lay is followed by a close-up of the
       * strap that is IN that flat lay, so it reads as a step closer rather
       * than a slideshow, and the three near-identical flat lays never run
       * back to back.
       *
       * THE FRAMES ARE NOT ALL THE SAME WIDTH, on purpose. The three flat lays
       * are the full width of their files, 2000 or 1536. The two strap shots
       * fill their frame edge to edge - a third of the copy zone was leather -
       * so they are cropped in until the copy lands on bare wall: 1280 and
       * 1460 wide. That is a 1.56x change of scale across the five, which is
       * the zoom the order was chosen for.
       *
       * THE COPY COLUMN STARTS FURTHER RIGHT HERE than on any other slide,
       * 0.77 instead of 0.709, because the kit components run out to 0.734 on
       * the flat lays - further than any other product in the hero. The button
       * was shortened from "SHOP THE ANALOG KIT" to "SHOP THE KIT" to make
       * that possible: at 225px the old label set a floor on the column width
       * that pushed the copy back onto the split rings below about 1150px.
       *
       * BLACK COPY, set as text_color on the block. Behind the copy it is 99%
       * bare concrete and black measures 8.5 to 11.3:1 - the best of any hero
       * here, because the flat lays sit on the pale side of the backdrop.
       *
       * The old panel frames (m3-desk/m3-mob 1..5) are still in Shopify Files
       * and nothing points at them. The blue plastic opening pick is still in
       * the bottom centre of every flat lay; it ships in the kit, it is the
       * only saturated colour in the hero, and it cannot be cropped out
       * without losing the split rings with it. Flagged twice now and kept. */
      desk: { 1: 'anlg-desk-1.webp',   /* Silver Chrome kit, laid out */
              2: 'anlg-desk-2.webp',   /* that black strap, on the M3 */
              3: 'anlg-desk-3.webp',   /* Solid Brass kit, laid out */
              4: 'anlg-desk-4.webp',   /* that brown strap, on the M3 */
              5: 'anlg-desk-5.webp' }, /* Black Patina kit, laid out */
      mob:  { 1: 'anlg-mob-1.webp',  2: 'anlg-mob-2.webp',  3: 'anlg-mob-3.webp',
              4: 'anlg-mob-4.webp',  5: 'anlg-mob-5.webp' },
      split: 100,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'prokit',
      deskKey: 'prokit-q-hero-desktop',
      mobKey: 'prokit-q-hero-mobile',
      /* Added 2026-10-11, order chosen by Indre: tin, black, patina, OxBlood,
       * then the whole range to close.
       *
       * CROPPED AND NOTHING ELSE. The shoot is high key and the five sit
       * across a 1.30x range (0.393 / 0.355 / 0.308 / 0.402 / 0.392), so this
       * slide is brighter than the others and drifts slightly as it runs.
       *
       * Two graded versions were tried and both rejected: a plain gamma
       * darkened the whole picture, and because the parts are black on a black
       * camera it ate the edge highlights that separate them - 40% of the
       * detail inside the product. A luminance masked version that darkened
       * only the concrete kept the product intact but made the background look
       * artificial. */
      desk: { 1: 'pk-desk-1.webp', 2: 'pk-desk-2.webp', 3: 'pk-desk-3.webp',
              4: 'pk-desk-4.webp', 5: 'pk-desk-5.webp' },
      mob:  { 1: 'pk-mob-1.webp',  2: 'pk-mob-2.webp',  3: 'pk-mob-3.webp',
              4: 'pk-mob-4.webp',  5: 'pk-mob-5.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'm10',
      deskKey: 'm10n-desk-1',
      mobKey: 'm10n-mob-1',
      /* FULL BLEED from 2026-10-22, split 100, reshot in 3:2.
       *
       * The first set Indre sent for this were 2000x2000 SQUARES with the
       * camera dead centre, running 0.156 to 0.843 of the width - the product
       * alone taking 68% of the frame. Cropping cannot fix that: pushed hard
       * against the left edge the best any of them gave was 18.6% of clear
       * wall, 268px at 1440, against a copy column that needs about 420 with
       * its padding. Four of the five could not hold the copy at all. If
       * square files turn up again for a full bleed slide, that is the whole
       * answer - measure, say so, and ask for 3:2.
       *
       * These are the 3:2 versions of the same shoot and they work. Each is
       * cropped from the LEFT so the camera sits about 0.17 in from the edge,
       * which is what walks it leftward in frame; the tripod shot is the
       * exception and keeps its full width, because the legs splay and the
       * left one is already near the edge. Camera ends 0.555 to 0.668, copy
       * starts at 0.709, so 59 to 221px of bare wall at 1440. Frames run 1590
       * to 2000 wide, a 1.26x change of scale across the five.
       *
       * BLACK COPY, set as text_color on the block. The wall behind the copy
       * is the bright side of this backdrop, so black measures 8.0 to 9.4:1
       * and white 2.1 to 2.8:1.
       *
       * Order is Indre's from the panel version and still the right one: the
       * open base is the only frame that shows what the slide claims, a door
       * that opens with the plate still on. Then the camera whole, the grip's
       * shape, the clamp underneath, on the tripod.
       *
       * The old panel frames (m10-desk/m10-mob 1..5) and the withdrawn
       * darkened m10b- set are still in Shopify Files, unreferenced. */
      desk: { 1: 'm10n-desk-1.webp',   /* base open, battery and card out */
              2: 'm10n-desk-2.webp',   /* front three quarter */
              3: 'm10n-desk-3.webp',   /* side profile */
              4: 'm10n-desk-4.webp',   /* Arca clamp underneath */
              5: 'm10n-desk-5.webp' }, /* on the tripod */
      mob:  { 1: 'm10n-mob-1.webp',  2: 'm10n-mob-2.webp',  3: 'm10n-mob-3.webp',
              4: 'm10n-mob-4.webp',  5: 'm10n-mob-5.webp' },
      split: 100,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'case',
      deskKey: 'SlimCase_Black_Leather_options_ceac1d89',
      mobKey: 'SlimCase_Black_Leather_options_mobile_3cb1ba86',
      /* Added 2026-10-13. This slide stopped being about one product: Indre
       * asked for a COMPARISON of the two cases, each shown with the thing it
       * hides. Red is the FlexCase and holds an AirTag; black is the SlimCase
       * and holds a spare SD card.
       *
       * OPENS ON THE AIRTAG, Indre's order: the hook first, then the red case
       * in the air, then the spare card in the black one, then the black case
       * on the camera to close. The AirTag is the thing nobody expects a
       * leather half case to do, so it earns the opening frame; the two
       * products still read as two chapters, red then black.
       *
       * Four frames, so it closes by returning to frame 1 at 7.2s the way SL3
       * does, and the comparison loops rather than parking on the last one.
       *
       * The FILENAMES are in the order the photographs arrived, not the order
       * they run, so this map is deliberately reversed: 4, 3, 2, 1.
       *
       * Cropped only. The four sit across a 1.57x range (0.283 / 0.296 /
       * 0.325 / 0.207 in running order) - it ends on the darkest frame rather
       * than starting on it, so the box dims into the loop instead of jumping
       * bright on the first dissolve. */
      desk: { 1: 'case-desk-4.webp',   /* FlexCase on the M, AirTag and battery out */
              2: 'case-desk-3.webp',   /* the red case in the air */
              3: 'case-desk-2.webp',   /* the spare SD card in the black case */
              4: 'case-desk-1.webp' }, /* SlimCase on the M, from the base */
      mob:  { 1: 'case-mob-4.webp',  2: 'case-mob-3.webp',
              3: 'case-mob-2.webp',  4: 'case-mob-1.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 1, d: 7.2 }
      ]
    }
  ];

  var CSS =
    '@keyframes okAnimFade{from{opacity:0}to{opacity:1}}' +
    '.ok-sv{position:absolute;top:0;left:0;height:100%;object-fit:cover;z-index:2;' +
      'background:#000;pointer-events:none}' +
    '.ok-sp{position:absolute;top:0;right:0;height:100%;background:#101012;z-index:2;' +
      'pointer-events:none}' +
    '.ok-pw{position:absolute;top:0;left:0;height:100%;overflow:hidden;z-index:2;' +
      'pointer-events:none}' +
    '.ok-pl{position:absolute;inset:0;background-size:cover;background-position:50% 50%;opacity:0}' +
    '.ok-pl.ok-base{opacity:1}' +
    '.f-slideshow__slide.is-okrun .ok-pl:not(.ok-base){animation-name:okAnimFade;' +
      'animation-timing-function:linear;animation-fill-mode:both}' +
    '@media (prefers-reduced-motion:reduce){.f-slideshow__slide.is-okrun .ok-pl{animation:none}}';

  function injectCss() {
    if (document.getElementById('ok-anim-css')) return;
    var s = document.createElement('style');
    s.id = 'ok-anim-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* Find the slide that owns this item, by the filename of its background. */
  function findSlide(key) {
    if (!key) return null;
    var imgs = document.querySelectorAll('.f-slideshow__slide img');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('src') || '';
      if (src.indexOf(key) !== -1) {
        var slide = imgs[i].closest ? imgs[i].closest('.f-slideshow__slide') : null;
        if (slide) return slide;
      }
    }
    return null;
  }

  function panelLayer(file, step, fade) {
    var d = document.createElement('div');
    d.className = 'ok-pl';
    d.style.backgroundImage = 'url(' + CDN + file + ')';
    if (!step) { d.className += ' ok-base'; return d; }
    d.style.animationDuration = fade + 's';
    d.style.animationDelay = step.d + 's';
    return d;
  }

  function buildPanel(spec) {
    var slide = findSlide(spec.deskKey) || findSlide(spec.mobKey);
    if (!slide || slide.getAttribute('data-ok-panel')) return null;
    slide.setAttribute('data-ok-panel', spec.id);

    [['.f-slideshow__slide-bg', spec.desk, spec.split],
     ['.f-slideshow__slide-mobile', spec.mob, 100]].forEach(function (pair) {
      var host = slide.querySelector(pair[0]);
      var map = pair[1], width = pair[2];
      if (!host) return;

      var wrap = document.createElement('div');
      wrap.className = 'ok-pw';
      wrap.style.width = width + '%';
      wrap.appendChild(panelLayer(map[1], null, 0));
      spec.steps.forEach(function (step) {
        if (map[step.f]) wrap.appendChild(panelLayer(map[step.f], step, step.fd || spec.fade));
      });
      host.appendChild(wrap);

      /* the right-hand ground for the copy, desktop only: on mobile the theme
       * already puts the text below the picture instead of beside it */
      if (width < 100) {
        var panel = document.createElement('div');
        panel.className = 'ok-sp';
        panel.style.width = (100 - width) + '%';
        host.appendChild(panel);
      }
    });
    return slide;
  }

  function makeVideo(src, widthPct, focus) {
    var v = document.createElement('video');
    v.className = 'ok-sv';
    v.src = src;
    v.muted = true;              /* a hero clip may never make noise unasked */
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.preload = 'none';          /* nothing is fetched until we call play() */
    v.style.width = widthPct + '%';
    v.style.objectPosition = focus;
    return v;
  }

  function buildSplit(spec) {
    var slide = findSlide(spec.deskKey) || findSlide(spec.mobKey);
    if (!slide || slide.getAttribute('data-ok-split')) return null;
    slide.setAttribute('data-ok-split', spec.id);

    var out = { slide: slide, els: [] };

    var deskHost = slide.querySelector('.f-slideshow__slide-bg');
    if (deskHost) {
      var dv = makeVideo(spec.desk, spec.split, spec.focus);
      var panel = document.createElement('div');
      panel.className = 'ok-sp';
      panel.style.width = (100 - spec.split) + '%';
      deskHost.appendChild(dv);
      deskHost.appendChild(panel);
      out.els.push(dv);
    }

    var mobHost = slide.querySelector('.f-slideshow__slide-mobile');
    if (mobHost) {
      var mv = makeVideo(spec.mob, 100, spec.focus);
      mobHost.appendChild(mv);
      out.els.push(mv);
    }
    return out.els.length ? out : null;
  }

  function boot() {
    injectCss();

    var slides = [];
    PANELS.forEach(function (spec) {
      var el = buildPanel(spec);
      if (el) slides.push(el);
    });

    var splits = [];
    SPLITS.forEach(function (spec) {
      var s = buildSplit(spec);
      if (s) splits.push(s);
    });

    if (!slides.length && !splits.length) return;

    /* Both guarded, because sync() runs on every class change in the carousel
       and re-adding the class would restart a sequence mid-run, while the
       reflow in reset() is only needed when there is something to rewind. */
    function show(el) {
      if (el.classList.contains('is-okrun')) return;
      el.classList.add('is-okrun');
    }
    function reset(el) {
      if (!el.classList.contains('is-okrun')) return;
      el.classList.remove('is-okrun');
      void el.offsetWidth;   /* forces the animation to start over next time */
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* offsetParent is null when a parent is display:none, which is exactly how
     * the theme hides the breakpoint we are not on. */
    function onScreen(el) { return el.offsetParent !== null; }

    function visibleFraction(el) {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || 0;
      if (!r.height) return 0;
      return Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) / r.height;
    }

    function carousel(el) {
      return (el.closest && el.closest('.f-slideshow__slides')) || el.parentElement;
    }

    /* "Is this the slide being looked at." Until the carousel has marked one,
       every slide counts, so a theme that stops using is-selected leaves the
       heroes animating rather than frozen on a single frame. */
    function current(el) {
      if (el.classList.contains('is-selected')) return true;
      var box = carousel(el);
      return !(box && box.querySelector('.f-slideshow__slide.is-selected'));
    }

    function live(el) { return visibleFraction(el) >= 0.35 && current(el); }

    function sync() {
      slides.forEach(function (el) { if (live(el)) show(el); else reset(el); });
      if (reduce) return;
      /* Play only the rendition for the breakpoint actually being shown, and
         only while its slide is the one on screen, so the other rendition is
         never downloaded and nothing plays out of sight. */
      splits.forEach(function (s) {
        var on = live(s.slide);
        s.els.forEach(function (v) {
          if (on && onScreen(v)) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            try { v.pause(); } catch (e) {}
          }
        });
      });
    }

    var watched = slides.concat(splits.map(function (s) { return s.slide; }));

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(sync, { threshold: [0, 0.35, 0.7, 1] });
      watched.forEach(function (el) { io.observe(el); });
    }

    /* The carousel advances by swapping the is-selected class, which no
       observer reports and no event in this theme announces, so watch the
       attribute itself. One observer on the container covers every slide. */
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(sync), seen = [];
      watched.forEach(function (el) {
        var box = carousel(el);
        if (!box || seen.indexOf(box) !== -1) return;
        seen.push(box);
        mo.observe(box, { attributes: true, attributeFilter: ['class'], subtree: true });
      });
    }

    var t = null;
    function later() {
      if (t) return;
      t = window.setTimeout(function () { t = null; sync(); }, 120);
    }
    window.addEventListener('scroll', later, { passive: true });
    window.addEventListener('resize', later, { passive: true });

    /* Two passes after first paint: an observer can miss an element that is
     * already on screen while the tab is hidden during load, and flickity sets
     * is-selected some way after this script runs. */
    window.setTimeout(sync, 300);
    window.setTimeout(sync, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
